import { useState, useEffect } from 'react';
import type {
  BrowseBehaviorResult,
  DateRange,
  FunnelResult,
  GeoCrossRefResult,
  PainPointsResult,
  PerformanceResult,
  RevenueOverviewResult,
  WidgetTelemetryFilters,
} from '../../../../api/widgetTelemetry';
import {
  fetchBrowseBehavior,
  fetchFunnel,
  fetchGeoCrossRef,
  fetchPainPoints,
  fetchPerformance,
  fetchRevenueOverview,
} from '../../../../api/widgetTelemetry';
import DateRangeSelector, { daysAgo } from '../shared/DateRangeSelector';
import FiltersBar from './FiltersBar';
import FunnelOverview from './FunnelOverview';
import PainPoints from './PainPoints';
import Performance from './Performance';
import BrowseBehavior from './BrowseBehavior';
import GeoCrossRef from './GeoCrossRef';
import RevenueOverview from './RevenueOverview';

type Section = 'funnel' | 'browse' | 'pain' | 'performance' | 'geo' | 'revenue';

const SECTIONS: { id: Section; label: string }[] = [
  { id: 'funnel', label: 'Funnel' },
  { id: 'browse', label: 'Browse Behavior' },
  { id: 'pain', label: 'Pain Points' },
  { id: 'performance', label: 'Performance' },
  { id: 'geo', label: 'Geo' },
  { id: 'revenue', label: 'Revenue' },
];

/**
 * Top-level widget telemetry view. Fetches all three panels in parallel and
 * lets the user switch between them via a secondary tab strip. The date range
 * + filter bar is shared state — changing either triggers a re-fetch.
 */
export default function WidgetTelemetry() {
  const [dateRange, setDateRange] = useState<DateRange>(daysAgo(30));
  const [filters, setFilters] = useState<WidgetTelemetryFilters>({});
  const [section, setSection] = useState<Section>('funnel');

  const [funnel, setFunnel] = useState<FunnelResult | null>(null);
  const [painPoints, setPainPoints] = useState<PainPointsResult | null>(null);
  const [performance, setPerformance] = useState<PerformanceResult | null>(null);
  const [browse, setBrowse] = useState<BrowseBehaviorResult | null>(null);
  const [geo, setGeo] = useState<GeoCrossRefResult | null>(null);
  const [revenue, setRevenue] = useState<RevenueOverviewResult | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchFunnel(dateRange, filters),
      fetchPainPoints(dateRange, filters),
      fetchPerformance(dateRange, filters),
      fetchBrowseBehavior(dateRange, filters),
      fetchGeoCrossRef(dateRange, filters),
      fetchRevenueOverview(dateRange, filters),
    ])
      .then(([f, p, perf, b, g, r]) => {
        setFunnel(f);
        setPainPoints(p);
        setPerformance(perf);
        setBrowse(b);
        setGeo(g);
        setRevenue(r);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [dateRange, filters]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">
          Booking Widget Telemetry
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <FiltersBar value={filters} onChange={setFilters} />
          <DateRangeSelector value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-stone-200 dark:border-stone-700">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${
              section === s.id
                ? 'border-moss-600 text-moss-600 dark:text-moss-400'
                : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-bark-900 dark:hover:text-stone-200'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'funnel' && <FunnelOverview data={funnel} loading={loading} />}
      {section === 'browse' && <BrowseBehavior data={browse} loading={loading} />}
      {section === 'pain' && <PainPoints data={painPoints} loading={loading} />}
      {section === 'performance' && <Performance data={performance} loading={loading} />}
      {section === 'geo' && <GeoCrossRef data={geo} loading={loading} />}
      {section === 'revenue' && <RevenueOverview data={revenue} loading={loading} />}
    </div>
  );
}
